import { useState, useEffect, useCallback } from 'react'
import { TextField } from '@mui/material'
import { Control, Controller, FieldPath, FieldValues, ControllerRenderProps } from 'react-hook-form'

/**
 * 数値フィールドの種類を定義するconst object
 * type aliasよりも型安全で、ランタイム値としても使用可能
 */
export const NUMBER_FIELD_TYPES = {
  NUMBER: 'number',
  AMOUNT: 'amount',
} as const

/** 数値フィールドの種類 */
export type NumberFieldType = typeof NUMBER_FIELD_TYPES[keyof typeof NUMBER_FIELD_TYPES]

/**
 * フィールドサイズを定義するconst object
 */
export const TEXT_SIZES = {
  XS: 'xs',
  SM: 'sm',
  MD: 'md',
  LG: 'lg',
} as const

/** フィールドサイズ */
export type TextSize = typeof TEXT_SIZES[keyof typeof TEXT_SIZES]

/**
 * フィールド幅を定義するconst object
 * サイズに対応する具体的なピクセル値またはCSS値
 */
const FIELD_WIDTHS = {
  xs: 120,
  sm: 240,
  md: 360,
  lg: 480,
  default: '100%',
} as const

/** フィールド幅の戻り値型 */
type FieldWidth = typeof FIELD_WIDTHS[keyof typeof FIELD_WIDTHS]

/** 全角→半角変換のオフセット値（Unicode仕様） */
const FULLWIDTH_TO_HALFWIDTH_OFFSET = 0xfee0 as const

/** NumberControlコンポーネントのProps */
interface NumberControlProps<T extends FieldValues> {
  readonly type?: NumberFieldType
  readonly size?: TextSize
  readonly isReadOnly?: boolean
  readonly placeholder?: string
  readonly name: FieldPath<T>
  readonly control: Control<T>
  readonly error?: string
  readonly suffix?: string
  readonly disabled?: boolean
}

/** getFieldWidthの引数型 */
interface GetFieldWidthParams {
  readonly size?: TextSize
}

/**
 * サイズに応じたフィールド幅を返す
 * exhaustiveness checkを含む型安全な実装
 */
export function getFieldWidth({ size }: GetFieldWidthParams): FieldWidth {
  if (size === undefined) {
    return FIELD_WIDTHS.default
  }

  switch (size) {
    case TEXT_SIZES.XS:
      return FIELD_WIDTHS.xs
    case TEXT_SIZES.SM:
      return FIELD_WIDTHS.sm
    case TEXT_SIZES.MD:
      return FIELD_WIDTHS.md
    case TEXT_SIZES.LG:
      return FIELD_WIDTHS.lg
    default: {
      // exhaustiveness check: 新しいサイズが追加された場合にコンパイルエラーになる
      const exhaustiveCheck: never = size
      return exhaustiveCheck
    }
  }
}

/** sanitizeNumericInputの引数型 - ランタイムで不正な値が渡される可能性を考慮 */
interface SanitizeNumericInputParams {
  readonly value: unknown
}

/**
 * 値が文字列かどうかを判定する型ガード
 */
function isString(value: unknown): value is string {
  return typeof value === 'string'
}

/**
 * 数値入力フィールド用の入力値サニタイズ関数
 *
 * onBlur時に全角文字を半角に変換する。
 * PC/スマートフォン両方で同一の動作をする。
 *
 * 変換仕様:
 * - 全角数字 (０-９): 半角数字 (0-9) に変換
 * - 全角カンマ (，): 除去（千桁区切り用）
 * - 半角カンマ (,): 除去（千桁区切り用）
 * - 全角ピリオド (．): 半角ピリオド (.) に変換（小数点対応）
 * - 全角マイナス (－): 半角マイナス (-) に変換（負数対応）
 * - 半角数字 (0-9): そのまま維持
 * - 半角ピリオド (.): そのまま維持（小数点）
 * - 半角マイナス (-): そのまま維持（負数）
 * - その他の文字: 除去
 *
 * @param value - サニタイズ対象の入力値（unknown型で防御的に受け取る）
 * @returns サニタイズ後の値（null/undefined/非文字列の場合は空文字を返す）
 */
export function sanitizeNumericInput({ value }: SanitizeNumericInputParams): string {
  if (!isString(value)) {
    return ''
  }
  return value
    .replace(/[０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - FULLWIDTH_TO_HALFWIDTH_OFFSET))
    .replace(/[，,]/g, '')
    .replace(/．/g, '.')
    .replace(/－/g, '-')
    .replace(/[^0-9.-]/g, '')
}

/** formatNumberの引数型 */
interface FormatNumberParams {
  readonly value: number | null | undefined
  readonly thousandSeparator: boolean
}

/** formatNumberWithSuffixの引数型 */
interface FormatNumberWithSuffixParams {
  readonly value: number | null | undefined
  readonly thousandSeparator: boolean
  readonly suffix?: string
}

/**
 * 数値を表示用文字列にフォーマットする（suffix付き）
 * 小数点・負数に対応（react-number-formatと同等の動作）
 * suffixは値の直後に付加される（react-number-formatと同じ表示方式）
 *
 * @param value - フォーマット対象の数値
 * @param thousandSeparator - 千桁区切りを適用するか
 * @param suffix - 接尾辞（例: '円'）
 * @returns フォーマット後の文字列
 */
export function formatNumberWithSuffix({ value, thousandSeparator, suffix }: FormatNumberWithSuffixParams): string {
  if (value == null) {
    return ''
  }
  let formatted: string
  if (thousandSeparator) {
    // 小数点以下の桁数を維持するため、maximumFractionDigitsを設定
    formatted = Intl.NumberFormat('ja-JP', { maximumFractionDigits: 20 }).format(value)
  } else {
    formatted = String(value)
  }
  // suffixを値の直後に付加（react-number-formatと同じ動作）
  if (suffix) {
    formatted += suffix
  }
  return formatted
}

/**
 * 数値を表示用文字列にフォーマットする
 * 小数点・負数に対応（react-number-formatと同等の動作）
 *
 * @param value - フォーマット対象の数値
 * @param thousandSeparator - 千桁区切りを適用するか
 * @returns フォーマット後の文字列
 */
export function formatNumber({ value, thousandSeparator }: FormatNumberParams): string {
  return formatNumberWithSuffix({ value, thousandSeparator, suffix: undefined })
}

/** parseNumberの引数型 */
interface ParseNumberParams {
  readonly value: string
  readonly suffix?: string
}

/**
 * 文字列を数値にパースする
 * 小数点・負数に対応（react-number-formatのfloatValueと同等の動作）
 * suffixが指定されている場合は、パース前にsuffixを除去する
 *
 * @param value - パース対象の文字列
 * @param suffix - 除去する接尾辞（例: '円'）
 * @returns パース後の数値（無効な場合はnull）
 */
export function parseNumber({ value, suffix }: ParseNumberParams): number | null {
  // suffixを除去（react-number-formatと同じ動作）
  let processedValue = value
  if (suffix && processedValue.endsWith(suffix)) {
    processedValue = processedValue.slice(0, -suffix.length)
  }
  const sanitized = sanitizeNumericInput({ value: processedValue })
  if (sanitized === '' || sanitized === '-' || sanitized === '.') {
    return null
  }
  const num = parseFloat(sanitized)
  return Number.isNaN(num) ? null : num
}

/**
 * field.valueを安全に数値またはnullに変換する型ガード関数
 * React Hook Formのfield.valueは様々な型になりうるため、防御的に処理する
 */
function toNumberOrNull(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null
  }
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value
  }
  // 文字列の場合はパースを試みる
  if (typeof value === 'string') {
    const parsed = parseNumber({ value })
    return parsed
  }
  return null
}

/** NumberInputInnerコンポーネントのProps */
interface NumberInputInnerProps<T extends FieldValues> {
  readonly field: ControllerRenderProps<T, FieldPath<T>>
  readonly thousandSeparator: boolean
  readonly size?: TextSize
  readonly isReadOnly?: boolean
  readonly placeholder?: string
  readonly error?: string
  readonly suffix?: string
  readonly disabled?: boolean
}

/**
 * 数値入力の内部コンポーネント
 * Controllerのrender内でhooksを使用するため、別コンポーネントに分離
 */
function NumberInputInner<T extends FieldValues>({
  field,
  thousandSeparator,
  size,
  isReadOnly,
  placeholder,
  error,
  suffix,
  disabled,
}: NumberInputInnerProps<T>): JSX.Element {
  // 型安全なfield.value変換
  const initialValue = toNumberOrNull(field.value)
  const [displayValue, setDisplayValue] = useState<string>(() =>
    formatNumberWithSuffix({ value: initialValue, thousandSeparator, suffix })
  )

  // field.valueが外部から変更された場合に表示値を同期
  useEffect(() => {
    const numericValue = toNumberOrNull(field.value)
    const formatted = formatNumberWithSuffix({ value: numericValue, thousandSeparator, suffix })
    setDisplayValue(formatted)
  }, [field.value, thousandSeparator, suffix])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    // onChangeでは値を変換せず、そのまま表示（IME対応）
    setDisplayValue(e.target.value)
  }, [])

  const handleBlur = useCallback((): void => {
    // onBlurで全角→半角変換、数値パース、フォーマット適用
    // suffix付きの値からsuffixを除去してパース
    const parsed = parseNumber({ value: displayValue, suffix })
    const formatted = formatNumberWithSuffix({ value: parsed, thousandSeparator, suffix })
    setDisplayValue(formatted)
    field.onChange(parsed)
    field.onBlur()
  }, [displayValue, thousandSeparator, suffix, field])

  const hasError = error !== undefined

  return (
    <TextField
      inputRef={field.ref}
      size="small"
      placeholder={placeholder}
      sx={{ width: getFieldWidth({ size }), textAlign: 'right' }}
      InputProps={{
        readOnly: isReadOnly,
        inputProps: { style: { textAlign: 'right' } },
      }}
      error={hasError}
      helperText={error ?? ''}
      disabled={disabled}
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  )
}

/**
 * 数値入力コンポーネント（React Hook Form連携）
 *
 * react-number-formatを使用せず、onBlurのみで値の変換・フォーマットを行う。
 * これによりIME入力時の問題を回避する。
 *
 * 動作仕様:
 * - onChange: 入力値をそのまま表示（変換しない）
 * - onBlur: 全角→半角変換、数値パース、フォーマット適用
 */
export default function NumberControl<T extends FieldValues>({
  type,
  size,
  isReadOnly,
  placeholder,
  name,
  control,
  error,
  suffix,
  disabled,
}: NumberControlProps<T>): JSX.Element {
  const thousandSeparator = type === NUMBER_FIELD_TYPES.AMOUNT

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <NumberInputInner
          field={field}
          thousandSeparator={thousandSeparator}
          size={size}
          isReadOnly={isReadOnly}
          placeholder={placeholder}
          error={error}
          suffix={suffix}
          disabled={disabled}
        />
      )}
    />
  )
}
